import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  TextField,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Popover,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import {
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  ArrowUpDown,
  RefreshCw,
  Building2,
  Filter,
} from "lucide-react";
import { fetchPipeline } from "../../../../../../redux/actions/marketingAction";
import OrganizationFiche from "./OrganizationFiche/OrganizationFiche";

const STATUS_CONFIG = {
  lead: { label: "Lead", color: "#6b7280" },
  testing: { label: "Testing", color: "#3b82f6" },
  demo_booked: { label: "Demo Booked", color: "#8b5cf6" },
  proposal: { label: "Proposal", color: "#f59e0b" },
  closed: { label: "Closed", color: "#10b981" },
  lost: { label: "Lost", color: "#ef4444" },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const PipelineTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pipeline, loading } = useSelector((state) => state.marketing);

  const [statusFilters, setStatusFilters] = useState([]);
  const [sortBy, setSortBy] = useState("estimated_value");
  const [page, setPage] = useState(0);
  const [ficheOrgId, setFicheOrgId] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);

  const toggleStatus = (status) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(0);
  };

  const DEFAULT_STATUSES = ["testing", "demo_booked", "proposal", "closed", "lost"];

  const loadData = useCallback(() => {
    const activeStatuses = statusFilters.length > 0 ? statusFilters : DEFAULT_STATUSES;
    dispatch(fetchPipeline({
      status: activeStatuses.join(","),
      sort_by: sortBy,
      page: page + 1,
      page_size: 50,
    }));
  }, [dispatch, statusFilters, sortBy, page]);

  useEffect(() => { loadData(); }, [loadData]);

  const items = pipeline?.items || [];
  const summary = pipeline?.summary || {};
  const total = pipeline?.total || 0;

  const formatValue = (val, currency) => {
    if (!val) return "—";
    return `${Number(val).toLocaleString()} ${currency || "MAD"}`;
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5, flexShrink: 0 }}>
        {ALL_STATUSES.map((status) => {
          const cfg = STATUS_CONFIG[status];
          const data = summary[status] || { count: 0, total_value: 0 };
          return (
            <Paper
              key={status}
              elevation={0}
              onClick={() => toggleStatus(status)}
              sx={{
                p: 1.5, borderRadius: "10px", flex: 1, minWidth: 120, cursor: "pointer",
                border: `1px solid ${statusFilters.includes(status) ? alpha(cfg.color, 0.5) : theme.palette.divider}`,
                bgcolor: statusFilters.includes(status) ? alpha(cfg.color, 0.05) : "transparent",
                transition: "all 0.15s",
                "&:hover": { borderColor: alpha(cfg.color, 0.4) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.color }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.65rem", color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {cfg.label}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1 }}>
                {data.count}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "0.65rem", color: cfg.color, fontWeight: 600 }}>
                {data.total_value > 0 ? formatValue(data.total_value, "MAD") : "—"}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
          Pipeline ({total})
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ fontSize: "0.75rem", height: 32, minWidth: 130 }}
          >
            <MenuItem value="estimated_value" sx={{ fontSize: "0.75rem" }}>Valeur</MenuItem>
            <MenuItem value="score" sx={{ fontSize: "0.75rem" }}>Score</MenuItem>
            <MenuItem value="next_follow_up" sx={{ fontSize: "0.75rem" }}>Prochaine relance</MenuItem>
            <MenuItem value="created_at" sx={{ fontSize: "0.75rem" }}>Date création</MenuItem>
            <MenuItem value="name" sx={{ fontSize: "0.75rem" }}>Nom</MenuItem>
          </Select>
          <Tooltip title="Rafraîchir">
            <IconButton size="small" onClick={loadData}>
              <RefreshCw size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }}>Organisation</TableCell>
              <TableCell
                sx={{
                  fontWeight: 600, fontSize: "0.7rem", cursor: "pointer", userSelect: "none",
                  "&:hover": { color: theme.palette.primary.main },
                }}
                onClick={(e) => setStatusAnchor(e.currentTarget)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Status
                  <Filter size={11} style={{ opacity: statusFilters.length > 0 ? 1 : 0.3 }} />
                  {statusFilters.length > 0 && (
                    <Chip label={statusFilters.length} size="small" sx={{ height: 16, fontSize: "0.55rem", fontWeight: 700, minWidth: 16 }} />
                  )}
                </Box>
              </TableCell>
              <Popover
                open={Boolean(statusAnchor)}
                anchorEl={statusAnchor}
                onClose={() => setStatusAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { p: 1.5, borderRadius: "10px", minWidth: 180 } }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5, color: theme.palette.text.secondary, mb: 0.5, display: "block" }}>
                  Filtrer par status
                </Typography>
                {ALL_STATUSES.map((s) => {
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <FormControlLabel
                      key={s}
                      control={
                        <Checkbox
                          checked={statusFilters.includes(s)}
                          onChange={() => toggleStatus(s)}
                          size="small"
                          sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.color }} />
                          <Typography sx={{ fontSize: "0.75rem" }}>{cfg.label}</Typography>
                        </Box>
                      }
                      sx={{ display: "flex", mx: 0, py: 0.25 }}
                    />
                  );
                })}
                {statusFilters.length > 0 && (
                  <Typography
                    variant="caption"
                    onClick={() => { setStatusFilters([]); setStatusAnchor(null); }}
                    sx={{ display: "block", textAlign: "center", mt: 0.5, cursor: "pointer", color: theme.palette.primary.main, fontSize: "0.7rem", "&:hover": { textDecoration: "underline" } }}
                  >
                    Effacer filtres
                  </Typography>
                )}
              </Popover>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }} align="right">Valeur</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }} align="center">Contacts</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }} align="center">Score</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }}>Prochaine tâche</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }}>Relance</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem" }}>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                  <Building2 size={32} strokeWidth={1} color={theme.palette.text.disabled} />
                  <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.disabled }}>
                    Aucune organisation dans le pipeline
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((org) => {
                const statusCfg = STATUS_CONFIG[org.status] || STATUS_CONFIG.lead;
                return (
                  <TableRow
                    key={org.id}
                    hover
                    onClick={() => setFicheOrgId(org.id)}
                    sx={{ cursor: "pointer", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Building2 size={14} color={theme.palette.text.secondary} strokeWidth={1.5} />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }} noWrap>
                          {org.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusCfg.label}
                        size="small"
                        sx={{
                          height: 22, fontSize: "0.65rem", fontWeight: 600, borderRadius: "6px",
                          bgcolor: alpha(statusCfg.color, 0.1), color: statusCfg.color,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: org.estimated_value ? 600 : 400, color: org.estimated_value ? theme.palette.text.primary : theme.palette.text.disabled }}>
                        {formatValue(org.estimated_value, org.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{org.contact_count}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {org.score > 0 ? (
                        <Chip
                          icon={<TrendingUp size={10} />}
                          label={org.score}
                          size="small"
                          sx={{
                            height: 20, fontSize: "0.6rem", fontWeight: 700,
                            bgcolor: alpha("#10b981", 0.1), color: "#10b981",
                            "& .MuiChip-icon": { color: "#10b981" },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "0.8rem", color: theme.palette.text.disabled }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: org.next_task_date ? (new Date(org.next_task_date) < new Date() ? "#ef4444" : theme.palette.text.primary) : theme.palette.text.disabled }}>
                        {formatDate(org.next_task_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
                        {formatDate(org.next_follow_up)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, textTransform: "capitalize" }}>
                        {org.source || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Active filters indicator */}
      {statusFilters.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}>
            Filtres:
          </Typography>
          {statusFilters.map((s) => (
            <Chip
              key={s}
              label={STATUS_CONFIG[s]?.label || s}
              size="small"
              onDelete={() => toggleStatus(s)}
              sx={{
                height: 20, fontSize: "0.6rem", fontWeight: 600, borderRadius: "6px",
                bgcolor: alpha(STATUS_CONFIG[s]?.color || "#6b7280", 0.1),
                color: STATUS_CONFIG[s]?.color || "#6b7280",
              }}
            />
          ))}
          <Chip
            label="Effacer"
            size="small"
            onClick={() => setStatusFilters([])}
            sx={{ height: 20, fontSize: "0.6rem", borderRadius: "6px" }}
          />
        </Box>
      )}

      {total > 50 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          sx={{ flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}` }}
        />
      )}

      {/* Fiche Drawer */}
      <OrganizationFiche
        open={Boolean(ficheOrgId)}
        onClose={() => { setFicheOrgId(null); loadData(); }}
        orgId={ficheOrgId}
      />
    </Box>
  );
};

export default PipelineTable;
